import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Grid,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import GlassCard from "../../General/GlassCard";
import LoadingState from "../../General/LoadingState";
import AppDialog from "../../General/Dialog/AppDialog";
import TaskFilters from "./TaskFilters";
import TaskLegalFilesTable from "./TaskLegalFilesTable";
import TaskActivityTimeline from "./TaskActivityTimeline";
import { useToast } from "../../../context/ToastContext";
import { useAuth } from "../../../context/AuthContext";
import {
  getWorkspaceTaskById,
  getWorkspaceTaskLegalFiles,
  exportWorkspaceTaskReport,
  updateWorkspaceTaskStatus,
  reassignWorkspaceTask,
} from "../../../services/WorkspaceService";
import { getCustomStatusOptions } from "../../../services/CustomStatusService";
import { getLegalFileStages } from "../../../services/LegalFileStageService";
import { getUsers } from "../../../services/UserService";
import {
  timeAgo,
  initials,
  personName,
  statusMeta,
  nextStatuses,
  TASK_STATUS,
} from "../workspaceUtils";
import FolderCopyRoundedIcon from "@mui/icons-material/FolderCopyRounded";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import HistoryRoundedIcon from "@mui/icons-material/HistoryRounded";

const SYSTEM_STATE_SCOPE = "lawsuit_tax";

export default function WorkspaceTaskDetail() {
  const { id } = useParams();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [task, setTask] = useState(null);
  const [loadingTask, setLoadingTask] = useState(true);

  const [systemStates, setSystemStates] = useState([]);
  const [loadingStates, setLoadingStates] = useState(true);

  const [stages, setStages] = useState([]);
  const [loadingStages, setLoadingStages] = useState(true);

  const [legalFiles, setLegalFiles] = useState([]);
  const [loadingLegalFiles, setLoadingLegalFiles] = useState(false);

  const [selectedStates, setSelectedStates] = useState([]);
  const [selectedLawyerIds, setSelectedLawyerIds] = useState([]);
  const [selectedStageIds, setSelectedStageIds] = useState([]);
  const [operationsLimit, setOperationsLimit] = useState(0);
  const [exporting, setExporting] = useState(false);

  const [savingStatus, setSavingStatus] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const [users, setUsers] = useState([]);
  const [reassignAnchor, setReassignAnchor] = useState(null);

  const fetchTask = useCallback(
    async (withSpinner = true) => {
      if (withSpinner) setLoadingTask(true);
      try {
        const res = await getWorkspaceTaskById(id);
        setTask(res.data?.data || res.data);
      } catch (error) {
        console.error("[WorkspaceTaskDetail] Error al cargar la tarea:", error);
        showToast("No se pudo cargar la tarea.", "error");
      } finally {
        if (withSpinner) setLoadingTask(false);
      }
    },
    [id, showToast],
  );

  useEffect(() => {
    fetchTask();
  }, [fetchTask]);

  const canReassign = !!task?.permissions?.can_reassign;

  useEffect(() => {
    if (!canReassign) return;
    let active = true;
    getUsers()
      .then((res) => {
        if (!active) return;
        const list = res.data?.data ?? res.data;
        setUsers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (active) setUsers([]);
      });
    return () => {
      active = false;
    };
  }, [canReassign]);

  const handleChangeStatus = async (nextStatus) => {
    setSavingStatus(true);
    try {
      await updateWorkspaceTaskStatus(id, { status: nextStatus });
      await fetchTask(false);
      showToast("Estado actualizado.", "success");
    } catch (error) {
      showToast(
        error?.response?.data?.message || "No se pudo cambiar el estado de la tarea.",
        "error",
      );
    } finally {
      setSavingStatus(false);
    }
  };

  const handleReassign = async (userId) => {
    setReassignAnchor(null);
    if (!userId || userId === task?.assigned_to?.id) return;
    try {
      await reassignWorkspaceTask(id, { assigned_to: userId });
      await fetchTask(false);
      showToast("Tarea reasignada.", "success");
    } catch (error) {
      showToast(
        error?.response?.data?.message || "No se pudo reasignar la tarea.",
        "error",
      );
    }
  };

  useEffect(() => {
    async function fetchStates() {
      setLoadingStates(true);
      try {
        const res = await getCustomStatusOptions(SYSTEM_STATE_SCOPE);
        setSystemStates(res.data?.data || []);
      } catch (error) {
        console.error("[WorkspaceTaskDetail] Error al cargar filtros:", error);
        showToast("No se pudieron cargar los filtros.", "error");
      } finally {
        setLoadingStates(false);
      }
    }
    fetchStates();
  }, [showToast]);

  useEffect(() => {
    async function fetchStages() {
      setLoadingStages(true);
      try {
        const res = await getLegalFileStages();
        const data = (res.data?.data || []).slice().sort((a, b) => a.order - b.order);
        setStages(data);
      } catch (error) {
        // La etapa es solo un filtro auxiliar. Si el usuario no tiene acceso al
        // módulo de etapas, degradamos en silencio (el filtro queda vacío).
        console.warn("[WorkspaceTaskDetail] No se pudieron cargar las etapas:", error);
        setStages([]);
      } finally {
        setLoadingStages(false);
      }
    }
    fetchStages();
  }, [showToast]);

  const fetchLegalFiles = useCallback(async () => {
    setLoadingLegalFiles(true);
    try {
      const res = await getWorkspaceTaskLegalFiles(id, {
        operations_limit: operationsLimit,
      });
      setLegalFiles(res.data?.data || []);
    } catch (error) {
      console.error("[WorkspaceTaskDetail] Error al cargar expedientes:", error);
      showToast("No se pudieron cargar los expedientes de la tarea.", "error");
    } finally {
      setLoadingLegalFiles(false);
    }
  }, [id, operationsLimit, showToast]);

  useEffect(() => {
    fetchLegalFiles();
  }, [fetchLegalFiles]);

  const lawyers = useMemo(() => {
    const byId = new Map();
    legalFiles.forEach((lf) => {
      if (lf.lawyer?.id && !byId.has(lf.lawyer.id)) {
        byId.set(lf.lawyer.id, lf.lawyer);
      }
    });
    return Array.from(byId.values()).sort((a, b) =>
      `${a.name} ${a.lastname}`.localeCompare(`${b.name} ${b.lastname}`),
    );
  }, [legalFiles]);

  const filteredLegalFiles = useMemo(() => {
    return legalFiles.filter((lf) => {
      const stateOk =
        selectedStates.length === 0 ||
        (lf.system_state && selectedStates.includes(lf.system_state.id));
      const lawyerOk =
        selectedLawyerIds.length === 0 ||
        selectedLawyerIds.includes(lf.lawyer?.id);
      const stageOk =
        selectedStageIds.length === 0 ||
        (lf.stage && selectedStageIds.includes(lf.stage.id));
      return stateOk && lawyerOk && stageOk;
    });
  }, [legalFiles, selectedStates, selectedLawyerIds, selectedStageIds]);

  const handleToggleState = (key) => {
    setSelectedStates((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key],
    );
  };

  const handleToggleLawyer = (id) => {
    setSelectedLawyerIds((prev) =>
      prev.includes(id) ? prev.filter((l) => l !== id) : [...prev, id],
    );
  };

  const handleToggleStage = (id) => {
    setSelectedStageIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id],
    );
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await exportWorkspaceTaskReport(id, {
        states: selectedStates,
        lawyer_ids: selectedLawyerIds,
        legal_file_stage_ids: selectedStageIds,
        operations_limit: operationsLimit,
      });

      const blobType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([response.data], { type: blobType });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_workspace_tarea_${id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[WorkspaceTaskDetail] Error al exportar:", error);
      showToast("Ocurrió un error al generar el reporte.", "error");
    } finally {
      setExporting(false);
    }
  };

  if (loadingTask) {
    return <LoadingState text="Cargando tarea..." />;
  }

  const creatorName = personName(task?.created_by) || "Usuario";
  const assigneeName = personName(task?.assigned_to);

  const legalFileCount = Array.isArray(task?.legal_file_codes) ? task.legal_file_codes.length : 0;
  const borderCol = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.07)";

  const sMeta = statusMeta(task?.status);
  const moves = task?.permissions?.can_change_status ? nextStatuses(task?.status) : [];

  return (
    <Grid container spacing={2} justifyContent="center" sx={{ mt: 4 }}>
      <Grid size={{ xs: 12 }}>
        <GlassCard>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 1.8 }}>
            <Typography
              sx={{ flex: 1, fontWeight: 800, fontSize: 19, lineHeight: 1.4, whiteSpace: "pre-wrap" }}
            >
              {task?.text}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, flexShrink: 0 }}>
              <Chip
                size="small"
                label={task?.status_label || sMeta.single}
                sx={{
                  height: 26,
                  borderRadius: 999,
                  bgcolor: sMeta.bg,
                  color: sMeta.color,
                  "& .MuiChip-label": { px: 1.2, fontWeight: 800, fontSize: 12 },
                }}
              />
              <Tooltip title={task?.created_at ? new Date(task.created_at).toLocaleString() : ""}>
                <Chip
                  size="small"
                  label={timeAgo(task?.created_at)}
                  sx={{
                    height: 26,
                    borderRadius: 999,
                    bgcolor: isDark ? "rgba(255,255,255,0.07)" : "rgba(0,0,0,0.045)",
                    "& .MuiChip-label": { px: 1.2, fontWeight: 700, fontSize: 12, opacity: 0.75 },
                  }}
                />
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1.8 }}>
            <Button
              size="small"
              variant="text"
              onClick={() => setShowActivity(true)}
              startIcon={<HistoryRoundedIcon />}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
            >
              Ver actividad
              {Array.isArray(task?.activities) && task.activities.length > 0
                ? ` (${task.activities.length})`
                : ""}
            </Button>
            {(moves.length > 0 || canReassign) && (
              <>
              {moves.map((next) => {
                const reopen =
                  next === TASK_STATUS.IN_PROGRESS && task?.status === TASK_STATUS.FINISHED;
                return (
                  <Button
                    key={next}
                    size="small"
                    variant="contained"
                    disableElevation
                    disabled={savingStatus}
                    onClick={() => handleChangeStatus(next)}
                    startIcon={reopen ? <ReplayRoundedIcon /> : <ArrowForwardRoundedIcon />}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                  >
                    {reopen ? "Reabrir tarea" : `Mover a "${statusMeta(next).single}"`}
                  </Button>
                );
              })}

              {canReassign && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={(e) => setReassignAnchor(e.currentTarget)}
                    startIcon={<SwapHorizRoundedIcon />}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
                  >
                    Reasignar
                  </Button>
                  <Menu
                    anchorEl={reassignAnchor}
                    open={!!reassignAnchor}
                    onClose={() => setReassignAnchor(null)}
                  >
                    {users.length === 0 && <MenuItem disabled>Sin usuarios disponibles</MenuItem>}
                    {users.map((u) => (
                      <MenuItem
                        key={u.id}
                        selected={u.id === task?.assigned_to?.id}
                        onClick={() => handleReassign(u.id)}
                      >
                        {personName(u)}
                      </MenuItem>
                    ))}
                  </Menu>
                </>
              )}
              </>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
              mt: 2,
              pt: 1.6,
              borderTop: `1px solid ${borderCol}`,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.4, minWidth: 0, flexWrap: "wrap" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                <Tooltip title={assigneeName ? `Responsable: ${assigneeName}` : "Sin asignar"}>
                  <Avatar
                    sx={{ width: 24, height: 24, fontSize: 11, fontWeight: 800, bgcolor: "primary.main", color: "#fff" }}
                  >
                    {initials(assigneeName) || "?"}
                  </Avatar>
                </Tooltip>
                <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.85 }}>
                  {assigneeName || "Sin asignar"}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ opacity: 0.6, fontWeight: 700 }}>
                Creada por {creatorName}
              </Typography>
            </Box>

            {legalFileCount > 0 && (
              <Chip
                size="small"
                icon={<FolderCopyRoundedIcon sx={{ fontSize: "13px !important" }} />}
                label={`${legalFileCount} expediente${legalFileCount !== 1 ? "s" : ""}`}
                sx={{
                  height: 26,
                  borderRadius: 999,
                  flexShrink: 0,
                  bgcolor: "rgba(25,118,210,0.10)",
                  color: "primary.main",
                  "& .MuiChip-label": { px: 1, fontWeight: 800, fontSize: 12 },
                }}
              />
            )}
          </Box>
        </GlassCard>
      </Grid>

      <Grid size={{ xs: 12, md: 3.5 }}>
        <TaskFilters
          systemStates={systemStates}
          lawyers={lawyers}
          stages={stages}
          loadingStates={loadingStates}
          loadingLawyers={loadingLegalFiles}
          loadingStages={loadingStages}
          selectedStates={selectedStates}
          onToggleState={handleToggleState}
          selectedLawyerIds={selectedLawyerIds}
          onToggleLawyer={handleToggleLawyer}
          selectedStageIds={selectedStageIds}
          onToggleStage={handleToggleStage}
          operationsLimit={operationsLimit}
          onChangeOperationsLimit={setOperationsLimit}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 8.5 }}>
        <TaskLegalFilesTable
          rows={filteredLegalFiles}
          loading={loadingLegalFiles}
          onExport={handleExport}
          exporting={exporting}
          canExport={hasPermission("exportar reporte tarea")}
        />
      </Grid>

      <AppDialog
        open={showActivity}
        onClose={() => setShowActivity(false)}
        maxWidth="sm"
        title="Actividad de la tarea"
        description="Historial de cambios de estado, reasignaciones y creación."
        icon={<HistoryRoundedIcon />}
        actions={
          <Button
            onClick={() => setShowActivity(false)}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 800 }}
          >
            Cerrar
          </Button>
        }
      >
        <Box sx={{ mt: 1 }}>
          <TaskActivityTimeline activities={task?.activities || []} embedded />
        </Box>
      </AppDialog>
    </Grid>
  );
}
